import React, { useEffect, useState, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Community, CommunityMember } from '../../services/communityService';
import { useCommunity } from '../../contexts/CommunityContext';
import {
  canEditCommunity,
  canDeleteCommunity,
  mustTransferBeforeLeave,
  isOnlyCommunityMember,
  roleLabel,
  visibilityLabel,
} from '../../utils/communityPermissions';
import { CommunityMemberRow } from './CommunityMemberRow';
import { ConfirmRemoveMemberDialog } from './ConfirmRemoveMemberDialog';
import { EditCommunityModal } from './EditCommunityModal';
import { TransferOwnershipModal } from './TransferOwnershipModal';
import { DeleteCommunityDialog } from './DeleteCommunityDialog';
import { CommunityConfirmDialog } from './CommunityConfirmDialog';
import { getCommunityErrorMessage } from '../../utils/communityApiErrors';

function avatarColor(id: string): string {
  const colors = ['#d32f2f', '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#0288d1', '#455a64'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

interface Props {
  open: boolean;
  community: Community | null;
  actorClientId: string;
  onClose: () => void;
  /** Called after a successful leave — use for page-level cleanup only, not to re-prompt. */
  onAfterLeave?: () => void;
  onNotify: (message: string) => void;
}

export const CommunityInfoDrawer: React.FC<Props> = ({
  open,
  community,
  actorClientId,
  onClose,
  onAfterLeave,
  onNotify,
}) => {
  const {
    members,
    loadingMembers,
    membersNextCursor,
    loadMembers,
    removeMember,
    updateMemberRole,
    transferOwnership,
    deleteCommunity,
    leaveCommunity,
    uploadCommunityAvatar,
  } = useCommunity();

  const [editOpen, setEditOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<CommunityMember | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [makeOwnerTarget, setMakeOwnerTarget] = useState<CommunityMember | null>(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const myRole = community?.my_role;

  useEffect(() => {
    if (open && community) {
      loadMembers(community.community_id);
    }
  }, [open, community?.community_id, loadMembers]);

  const handleConfirmRemove = async () => {
    if (!community || !removeTarget) return;
    setRemoveLoading(true);
    try {
      await removeMember(community.community_id, removeTarget.client_id);
      onNotify(`${removeTarget.display_name || removeTarget.client_id} was removed`);
      setRemoveTarget(null);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message || 'Failed to remove member';
      onNotify(msg);
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleToggleModerator = async (member: CommunityMember, makeModerator: boolean) => {
    if (!community) return;
    try {
      await updateMemberRole(
        community.community_id,
        member.client_id,
        makeModerator ? 'moderator' : 'member'
      );
      onNotify(
        makeModerator
          ? `${member.display_name || member.client_id} is now an admin`
          : `${member.display_name || member.client_id} is no longer an admin`
      );
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message || 'Failed to update role';
      onNotify(msg);
    }
  };

  const handleAvatarClick = () => {
    if (!avatarLoading) avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !community) return;

    if (!file.type.startsWith('image/')) {
      onNotify('Please select an image file');
      return;
    }

    setAvatarLoading(true);
    try {
      await uploadCommunityAvatar(community.community_id, file);
      onNotify('Community image updated');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message || 'Failed to upload image';
      onNotify(msg);
    } finally {
      setAvatarLoading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleLeaveClick = () => {
    if (!community) return;
    if (mustTransferBeforeLeave(myRole, community.member_count)) {
      setTransferOpen(true);
      return;
    }
    setLeaveConfirmOpen(true);
  };

  const handleConfirmLeave = async () => {
    if (!community) return;
    setLifecycleLoading(true);
    try {
      await leaveCommunity(community.community_id);
      setLeaveConfirmOpen(false);
      onClose();
      onAfterLeave?.();
    } catch (err: unknown) {
      onNotify(getCommunityErrorMessage(err, 'Failed to leave community'));
    } finally {
      setLifecycleLoading(false);
    }
  };

  const handleTransferConfirm = async (newOwnerClientId: string) => {
    if (!community) return;
    setLifecycleLoading(true);
    try {
      await transferOwnership(community.community_id, newOwnerClientId);
      onNotify('Ownership transferred');
      setTransferOpen(false);
    } catch (err: unknown) {
      onNotify(getCommunityErrorMessage(err, 'Failed to transfer ownership'));
    } finally {
      setLifecycleLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!community) return;
    setLifecycleLoading(true);
    try {
      await deleteCommunity(community.community_id);
      onNotify('Community deleted');
      setDeleteOpen(false);
      onClose();
      onAfterLeave?.();
    } catch (err: unknown) {
      onNotify(getCommunityErrorMessage(err, 'Failed to delete community'));
    } finally {
      setLifecycleLoading(false);
    }
  };

  const handleMakeOwner = (member: CommunityMember) => {
    setMakeOwnerTarget(member);
  };

  const handleConfirmMakeOwner = async () => {
    if (!community || !makeOwnerTarget) return;
    const name = makeOwnerTarget.display_name || makeOwnerTarget.client_id;
    setLifecycleLoading(true);
    try {
      await transferOwnership(community.community_id, makeOwnerTarget.client_id);
      onNotify(`${name} is now the owner`);
      setMakeOwnerTarget(null);
    } catch (err: unknown) {
      onNotify(getCommunityErrorMessage(err, 'Failed to transfer ownership'));
    } finally {
      setLifecycleLoading(false);
    }
  };

  if (!community) return null;

  const showEdit = canEditCommunity(myRole);
  const showDelete = canDeleteCommunity(myRole);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 380 }, maxWidth: '100%' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Community info
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ px: 3, pb: 2, textAlign: 'center' }}>
            <Box sx={{ position: 'relative', width: 88, mx: 'auto', mb: 1.5 }}>
              <Avatar
                src={community.avatar_url}
                sx={{
                  width: 88,
                  height: 88,
                  bgcolor: avatarColor(community.community_id),
                  '& img': { objectFit: 'cover' },
                }}
              >
                {getInitials(community.name)}
              </Avatar>
              {showEdit && (
                <IconButton
                  onClick={handleAvatarClick}
                  disabled={avatarLoading}
                  aria-label="Change community image"
                  sx={{
                    position: 'absolute',
                    bottom: -4,
                    right: -4,
                    bgcolor: '#0f0f0f',
                    color: '#fff',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#333' },
                    '&:disabled': { bgcolor: '#999' },
                  }}
                >
                  {avatarLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PhotoCameraIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              )}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {community.name}
            </Typography>
            {community.description && (
              <Typography variant="body2" sx={{ color: '#5f6368', mt: 0.5 }}>
                {community.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1.5, flexWrap: 'wrap' }}>
              <Chip label={visibilityLabel(community.visibility)} size="small" />
              {myRole && (
                <Chip label={roleLabel(myRole)} size="small" variant="outlined" />
              )}
              <Chip
                label={`${community.member_count} member${community.member_count !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>

          <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {showEdit && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditOpen(true)}
                fullWidth
                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
              >
                Edit community
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<ExitToAppIcon />}
              onClick={handleLeaveClick}
              disabled={lifecycleLoading}
              fullWidth
              sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
            >
              {mustTransferBeforeLeave(myRole, community.member_count)
                ? 'Transfer ownership to leave'
                : 'Leave community'}
            </Button>
            {showDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setDeleteOpen(true)}
                disabled={lifecycleLoading}
                fullWidth
                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
              >
                Delete community
              </Button>
            )}
          </Box>

          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#5f6368' }}>
              Members
            </Typography>
            {loadingMembers && !members.length ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: '#9aa0a6' }} />
              </Box>
            ) : (
              <>
                {members.map((member) => (
                  <CommunityMemberRow
                    key={member.client_id}
                    member={member}
                    actorRole={myRole}
                    actorClientId={actorClientId}
                    onRemove={setRemoveTarget}
                    onToggleModerator={handleToggleModerator}
                    onMakeOwner={handleMakeOwner}
                  />
                ))}
                {membersNextCursor && (
                  <Button
                    size="small"
                    onClick={() => loadMembers(community.community_id, true)}
                    disabled={loadingMembers}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Load more
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Drawer>

      <EditCommunityModal
        open={editOpen}
        community={community}
        onClose={() => setEditOpen(false)}
        onSaved={() => onNotify('Community updated')}
      />

      <ConfirmRemoveMemberDialog
        open={Boolean(removeTarget)}
        member={removeTarget}
        loading={removeLoading}
        onConfirm={handleConfirmRemove}
        onCancel={() => !removeLoading && setRemoveTarget(null)}
      />

      <TransferOwnershipModal
        open={transferOpen}
        communityName={community.name}
        members={members}
        actorClientId={actorClientId}
        loading={lifecycleLoading}
        onConfirm={handleTransferConfirm}
        onClose={() => !lifecycleLoading && setTransferOpen(false)}
      />

      <DeleteCommunityDialog
        open={deleteOpen}
        communityName={community.name}
        loading={lifecycleLoading}
        onConfirm={handleDeleteConfirm}
        onClose={() => !lifecycleLoading && setDeleteOpen(false)}
      />

      <CommunityConfirmDialog
        open={leaveConfirmOpen}
        title={
          myRole === 'owner' && isOnlyCommunityMember(community.member_count)
            ? 'Leave and delete community?'
            : 'Leave community?'
        }
        description={
          myRole === 'owner' && isOnlyCommunityMember(community.member_count)
            ? `You are the only member of "${community.name}". Leaving will delete this community for everyone.`
            : `Leave "${community.name}"? You won't see new messages unless you rejoin.`
        }
        confirmLabel="Leave"
        confirmColor="error"
        loading={lifecycleLoading}
        onConfirm={handleConfirmLeave}
        onCancel={() => !lifecycleLoading && setLeaveConfirmOpen(false)}
      />

      <CommunityConfirmDialog
        open={Boolean(makeOwnerTarget)}
        title="Transfer ownership?"
        description={
          makeOwnerTarget
            ? `Make ${makeOwnerTarget.display_name || makeOwnerTarget.client_id} the owner of "${community.name}"? You will become a regular member.`
            : ''
        }
        confirmLabel="Make owner"
        loading={lifecycleLoading}
        onConfirm={handleConfirmMakeOwner}
        onCancel={() => !lifecycleLoading && setMakeOwnerTarget(null)}
      />
    </>
  );
};
